import TiptapLink from "@tiptap/extension-link";
import { Plugin, PluginKey, TextSelection } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";
import { getMarkRange } from "@tiptap/react";

export const Link = TiptapLink.extend({
  inclusive: false,

  parseHTML() {
    return [
      {
        tag: 'a[href]:not([data-type="button"]):not([href *= "javascript:" i])',
      },
    ];
  },

  addProseMirrorPlugins() {
    const { editor } = this;

    return [
      new Plugin({
        key: new PluginKey("linkClickHandler"),
        props: {
          handleKeyDown: (_: EditorView, event: KeyboardEvent) => {
            const { selection } = editor.state;

            if (event.key === "Escape" && selection.empty !== true) {
              editor.commands.focus(selection.to, { scrollIntoView: false });
            }

            return false;
          },
          handleClick(view, pos) {
            const { schema, doc, tr } = view.state;
            let range: ReturnType<typeof getMarkRange> | undefined;

            if (schema.marks.link) {
              range = getMarkRange(doc.resolve(pos), schema.marks.link);
            }

            if (!range) {
              return;
            }

            const { from, to } = range;
            const start = Math.min(from, to);
            const end = Math.max(from, to);

            if (pos < start || pos > end) {
              return;
            }

            const $start = doc.resolve(start);
            const $end = doc.resolve(end);
            const transaction = tr.setSelection(new TextSelection($start, $end));

            view.dispatch(transaction);
          },
        },
      }),
      new Plugin({
        key: new PluginKey("linkOrphanCleaner"),
        appendTransaction: (transactionList, _oldState, newState) => {
          const hasDocChanged = transactionList.some((tr) => tr.docChanged);

          if (!hasDocChanged) {
            return null;
          }

          const { selection, schema, storedMarks } = newState;

          if (!selection.empty) {
            return null;
          }

          const linkType = schema.marks.link;

          if (!linkType) {
            return null;
          }

          const marksAtCursor = storedMarks ?? selection.$from.marks();
          const hasLinkMark = marksAtCursor.some((mark) => mark.type === linkType);

          if (!hasLinkMark) {
            return null;
          }

          const $pos = selection.$from;
          const nodeBefore = $pos.nodeBefore;
          const nodeAfter = $pos.nodeAfter;

          const hasLinkBefore = nodeBefore?.marks.some((mark) => mark.type === linkType) === true;
          const hasLinkAfter = nodeAfter?.marks.some((mark) => mark.type === linkType) === true;

          if (hasLinkBefore || hasLinkAfter) {
            return null;
          }

          const cleanedMarks = marksAtCursor.filter((mark) => mark.type !== linkType);

          return newState.tr.setStoredMarks(cleanedMarks);
        },
      }),
    ];
  },
});

export default Link;
